import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { cloneList, DataModel, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../dto/dto.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { BusinessModuleRef } from './business-module-ref.js';

export class OpenAccessPassSeatLockUpdatedEvent extends SyncEvent {
  static readonly TYPE_ID: string = '175c6cf467364f8e90bb96fc5c58cde6';

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly businessRef: BusinessModuleRef,
      public readonly templateId: string,
      public readonly roomId: string,
      public readonly removedLocks: number[],
      public readonly addedLocks: number[],
  ) {
    super();
  }

  static create(args: {
    uniqueId: string,
    creatorId: string | null,
    deviceId: string | null,
    creationDate: dayjs.Dayjs,
    source: SyncEventSource,
    businessRef: BusinessModuleRef,
    templateId: string,
    roomId: string,
    removedLocks: number[],
    addedLocks: number[],
  }): OpenAccessPassSeatLockUpdatedEvent {
    return new OpenAccessPassSeatLockUpdatedEvent(
        args.uniqueId,
        args.creatorId,
        args.deviceId,
        args.creationDate,
        args.source,
        args.businessRef,
        args.templateId,
        args.roomId,
        args.removedLocks,
        args.addedLocks,
    );
  }

  get $typeId(): string {
    return OpenAccessPassSeatLockUpdatedEvent.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPassSeatLockUpdatedEvent {
    return new OpenAccessPassSeatLockUpdatedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.get<string>(map, 'templateId'),
        MapUtils.get<string>(map, 'roomId'),
        MapUtils.getList<number>(map, 'removedLocks', (v) => v as number),
        MapUtils.getList<number>(map, 'addedLocks', (v) => v as number),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'businessRef': this.businessRef.encode(),
      'templateId': this.templateId,
      'roomId': this.roomId,
      'removedLocks': this.removedLocks,
      'addedLocks': this.addedLocks,
    };
  }

  clone(): this {
    return new OpenAccessPassSeatLockUpdatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.businessRef.clone(),
        this.templateId,
        this.roomId,
        cloneList(this.removedLocks),
        cloneList(this.addedLocks),
    ) as this;
  }

  isSame(other: OpenAccessPassSeatLockUpdatedEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.templateId === this.templateId &&
        other.roomId === this.roomId &&
        isSameData(other.businessRef, this.businessRef) &&
        isSameList(other.removedLocks, this.removedLocks) &&
        isSameList(other.addedLocks, this.addedLocks);
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof OpenAccessPassSeatLockUpdatedEvent;
  }

  static assertType(dm: DataModel): dm is OpenAccessPassSeatLockUpdatedEvent {
    return dm instanceof OpenAccessPassSeatLockUpdatedEvent;
  }
}

export class OpenAccessPassSeatLockUpdatedEventBuilder extends DataModelBuilder<OpenAccessPassSeatLockUpdatedEvent> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPassSeatLockUpdatedEvent {
    return OpenAccessPassSeatLockUpdatedEvent.fromMap(context, map);
  }
}
